import type { FC } from "react";

import { usePageMetadata } from "../../hooks/usePageMetadata";
import { EmailOtpCard } from "./EmailOtpCard";

interface DashboardLoginProps {
  onRequestOtp: (email: string) => Promise<void>;
  onVerifyOtp: (
    email: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<void>;
}

export const DashboardLogin: FC<DashboardLoginProps> = ({
  onRequestOtp,
  onVerifyOtp,
}) => {
  usePageMetadata({
    description:
      "Secure passwordless sign-in for Arti. Access your AI engineering control plane with your approved work email.",
    keywords:
      "Arti login, Arti sign in, AI engineering dashboard login, passwordless engineering platform",
    robots: "noindex,nofollow",
    title: "Sign In | Arti",
    type: "website",
  });

  return (
    <div className="relative flex min-h-screen items-center overflow-hidden bg-arti-background px-6 py-8 text-slate-100">
      <div className="terminal-grid-bg absolute inset-0" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />

      <div className="relative mx-auto flex w-full max-w-md flex-col items-center">
        <div className="mb-6 flex flex-col items-center gap-2.5 text-center">
          <img src="/arti.svg" alt="Arti" className="h-12 w-12" />
          <div className="space-y-1.5">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-emerald-400">
              Arti Dashboard
            </p>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Sign in to Arti
            </h1>
            <p className="max-w-sm text-balance text-sm leading-6 text-slate-400">
              Your actors, channels, and runs — one control plane.
            </p>
          </div>
        </div>

        <EmailOtpCard
          onRequestOtp={onRequestOtp}
          onVerifyOtp={onVerifyOtp}
          surface="standalone"
          className="w-full"
          title="Access your workspace"
          subtitle="We'll email you a one-time sign-in code."
        />
      </div>
    </div>
  );
};

export default DashboardLogin;
